import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useSimpleSearch } from '../../utils/useSimpleSearch';
import { useDebounce } from '../../utils/useServerFiltering';
import { supabase } from '../../supabase';
import { 
  SearchableProjectDropdownProps, 
  ProjectOption, 
  MIN_SEARCH_LENGTH, 
  SEARCH_DEBOUNCE_DELAY, 
  MAX_SEARCH_RESULTS,
  NO_PROJECT_OPTION
} from './config';

const SearchableProjectDropdown: React.FC<SearchableProjectDropdownProps> = ({
  value,
  displayLabel,
  onChange,
  placeholder = "Search projects...",
  className = '',
  disabled = false,
  allowNone = false,
  noneLabel = 'No Project',
  required = false,
  entityType,
  minSearchLength = MIN_SEARCH_LENGTH,
  leadingItems
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [displayText, setDisplayText] = useState('');
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_DELAY);

  // Use simple search hook - now searches both projects and stages
  const { data: projects, loading } = useSimpleSearch<ProjectOption>(
    'search_timesheet_project_options',
    debouncedSearchTerm,
    minSearchLength,
    MAX_SEARCH_RESULTS
  );

  // Create dropdown items (copy exact structure from Dropdown component)
  const dropdownItems = useMemo(() => {
    const projectItems = projects
      .filter(project => !entityType || project.entity_type === entityType)
      .map(project => {
      // Format job number display for stages
      let displayJobNumber: string | number | bigint = project.job_number;
      if (project.entity_type === 'stage' && project.stage_id) {
        displayJobNumber = `${project.job_number}.${project.stage_id}`;
      }
      
      return {
        ...project,
        label: project.entity_type === 'stage' 
          ? `${displayJobNumber} - [${project.stage_description}] - ${project.parent_description}`
          : `${displayJobNumber} - ${project.display_name}`,
        value: project.id.toString()
      };
    });

    if (allowNone) {
      return [
        { ...NO_PROJECT_OPTION, company_name: noneLabel, label: noneLabel },
        ...projectItems
      ];
    }

    return projectItems;
  }, [projects, allowNone, noneLabel, entityType]);

  // Fixed choices ahead of the results: all of them on open, the matching
  // ones once something is typed.
  const leadingMatches = useMemo(() => {
    if (!leadingItems || leadingItems.length === 0) return [];
    const term = searchTerm.trim().toLowerCase();
    return term ? leadingItems.filter(item => item.label.toLowerCase().includes(term)) : leadingItems;
  }, [leadingItems, searchTerm]);

  const handleSelectLeading = (item: { id: string; label: string }) => {
    setDisplayText(item.label);
    onChange(item.id, null);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Handle selection (copy exact logic from Dropdown component)
  const handleSelect = (item: any) => {
    // Store display text for selected item
    setDisplayText(item.label);
    
    // Pass both the project ID and the full project data (now includes client info)
    const projectData = projects.find(p => p.id.toString() === item.value);
    onChange(item.value, projectData);
    setIsOpen(false);
    setSearchTerm(''); // Clear search to show selected value
  };

  // Fetch project details when value prop is provided (for initial display)
  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (value && value !== 'none' && displayLabel) {
        setDisplayText(displayLabel);
      } else if (value && value !== 'none' && typeof value === 'number') {
        setIsLoadingInitial(true);
        try {
          // Fetch the project by ID directly
          const { data, error } = await supabase
            .from('projects')
            .select('id, job_number, description, status')
            .eq('id', value)
            .single();

          if (error) throw error;

          if (data) {
            const label = `${data.job_number} - ${data.description}`;
            setDisplayText(label);
          }
        } catch (error) {
          console.error('Error fetching project details:', error);
          setDisplayText(`Project ID: ${value}`);
        } finally {
          setIsLoadingInitial(false);
        }
      } else if (value === null || value === 'none') {
        // Only show noneLabel if allowNone is true, otherwise leave empty for placeholder
        setDisplayText(allowNone ? noneLabel : '');
      } else if (!value) {
        setDisplayText(allowNone ? noneLabel : '');
      }
    };

    fetchProjectDetails();
  }, [value, displayLabel, noneLabel, allowNone]);

  // Initialize display text on mount
  useEffect(() => {
    if (!displayText && !value) {
      setDisplayText(allowNone ? noneLabel : '');
    }
  }, [allowNone, noneLabel, displayText, value]);

  // Get display value for selected item
  const displayValue = isLoadingInitial ? 'Loading...' : (displayText || placeholder);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm(''); // Clear search when closing
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`dropdown-container ${className} ${disabled ? 'disabled' : ''}`} ref={dropdownRef}>
      <div 
        className={`dropdown-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="selected-value">
          {displayValue}
        </div>
        <div className="dropdown-arrow">
          <svg 
            width="12" 
            height="8" 
            viewBox="0 0 12 8" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            className={isOpen ? 'rotated' : ''}
          >
            <path 
              d="M1 1.5L6 6.5L11 1.5" 
              stroke="currentColor" 
              strokeWidth="1.5" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {isOpen && (
        <div className="dropdown-menu">
          {/* Search input inside dropdown */}
          <div className="search-container">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by job number or description..."
              className="search-input dropdown-search-input"
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>

          {/* Items container */}
          <div className="items-container">
            {leadingMatches.map(item => (
              <div
                key={item.id}
                className={`dropdown-item dropdown-item--leading ${value === item.id ? 'selected' : ''}`}
                onClick={() => handleSelectLeading(item)}
              >
                {item.label}
              </div>
            ))}

            {/* Show "No Project" when no search or search too short */}
            {searchTerm.length < minSearchLength && allowNone && (
              <div
                className={`dropdown-item ${value === null ? 'selected' : ''}`}
                onClick={() => handleSelect({ id: 'none', label: noneLabel, value: null })}
              >
                {noneLabel}
              </div>
            )}

            {/* Show search hint */}
            {searchTerm.length > 0 && searchTerm.length < minSearchLength && (
              <div className="no-results">
                Type {minSearchLength - searchTerm.length} more character{searchTerm.length === minSearchLength - 1 ? '' : 's'} to search...
              </div>
            )}

            {/* Show loading */}
            {loading && searchTerm.length >= minSearchLength && (
              <div className="no-results">Searching projects...</div>
            )}

            {/* Show search results */}
            {!loading && searchTerm.length >= minSearchLength && (
              <>
                {/* Include "No Project" option in search results too */}
                {allowNone && (
                  <div
                    className={`dropdown-item ${value === null ? 'selected' : ''}`}
                    onClick={() => handleSelect({ id: 'none', label: noneLabel, value: null })}
                  >
                    {noneLabel}
                  </div>
                )}
                
                {/* Project results */}
                {dropdownItems.length > 0 ? (
                  dropdownItems
                    .filter(item => item.value !== null) // Exclude the "none" option since we show it separately
                    .map((item) => (
                      <div
                        key={item.id}
                        className={`dropdown-item ${item.value === value ? 'selected' : ''}`}
                        onClick={() => handleSelect(item)}
                      >
                        {item.label}
                      </div>
                    ))
                ) : leadingMatches.length === 0 ? (
                  <div className="no-results">No projects found matching "{searchTerm}"</div>
                ) : null}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableProjectDropdown;

export {}; 